import {z} from 'zod';
import {defineToolRegistryManifest} from '../../types/base-tool-types';
import {dataToolSchemas} from '../../utils/schemas';

const toolName = 'create-cms-collection-field' as const;

// All supported field types from PublicApiCollectionFieldType
const supportedFieldTypes = [
  'Color',
  'DateTime',
  'Email',
  'File',
  'Image',
  'Link',
  'MultiExternalFile',
  'MultiImage',
  'MultiReference',
  'Number',
  'Option',
  'Phone',
  'PlainText',
  'Reference',
  'RichText',
  'Switch',
  'VideoLink',
  'ExternalFile',
  'MembershipPlan',
  'Password',
  'Price',
  'SkuSettings',
  'SkuValues',
  'User',
] as const;

// Create a provider-compatible schema instead of discriminated union
// This ensures compatibility with all LLM providers (including Anthropic)
// while preserving the same validation logic
const argumentZod = z
  .object({
    collectionId: dataToolSchemas.collectionId,
    displayName: z.string().describe('Display name for the new field'),
    type: z
      .enum(supportedFieldTypes)
      .describe(
        'Field type - determines what kind of content this field can hold'
      ),
    isRequired: z
      .boolean()
      .optional()
      .describe('Whether the field is required (default: false)'),
    helpText: z
      .string()
      .optional()
      .describe('Help text for the field to guide content creators'),
    metadata: z
      .object({
        collectionId: dataToolSchemas.collectionId
          .optional()
          .describe(
            'The ID of the collection to reference (required for Reference/MultiReference fields)'
          ),
        options: z
          .array(
            z.object({
              name: z.string().describe('Display name for the option'),
            })
          )
          .optional()
          .describe('List of available options (required for Option fields)'),
      })
      .optional()
      .describe(
        'Additional metadata for specific field types (options for Option fields, collectionId for Reference fields)'
      ),
  })
  .refine(
    (data) => {
      if (data.type === 'Option') {
        return data.metadata?.options && data.metadata.options.length > 0;
      }
      return true;
    },
    {
      message:
        'Option fields must have at least one option in metadata.options',
      path: ['metadata', 'options'],
    }
  )
  .refine(
    (data) => {
      if (data.type === 'Reference' || data.type === 'MultiReference') {
        return data.metadata?.collectionId;
      }
      return true;
    },
    {
      message:
        'Reference fields must specify a referenced collection ID in metadata.collectionId',
      path: ['metadata', 'collectionId'],
    }
  );

export const createCMSCollectionFieldManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Create CMS collection field',
  family: 'cms',
  llmTool: {
    name: toolName,
    description:
      'Creates a new field in a CMS collection. Supports various field types including: PlainText, RichText, Image, Number, DateTime, Email, Phone, Link, Color, Switch, Option (dropdown/select), Reference (link to another collection), and many others. For Option fields, provide choices in metadata.options. For Reference/MultiReference fields, specify the target collection ID in metadata.collectionId. The field will be created in staging and requires publishing to go live.',
    inputSchema: argumentZod,
  },
  type: 'server',
  permission: [
    {
      resource: 'SiteCollection',
      action: 'manage',
      resolveResources: (args) => [
        {type: 'SiteCollection', id: args.collectionId},
      ],
    },
  ],
  confirmBeforeExecuting: false,
});

export type CreateCMSCollectionFieldArgumentType = z.infer<typeof argumentZod>;
